import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  legacyCasesService,
  type LegacyCaseFilters,
} from "../services/legacyCases.service";
import type {
  LegacyImportRequest,
  LegacySaveReviewRequest,
} from "../types/legacyCases";
import { prepareLegacyReview } from "../utils/prepareLegacyReview";

export function useLegacyCases(filters: LegacyCaseFilters) {
  return useQuery({
    queryKey: ["legacy-cases", filters],
    queryFn: () => legacyCasesService.list(filters),
  });
}

export function useLegacyCase(id: string | undefined) {
  return useQuery({
    queryKey: ["legacy-case", id],
    queryFn: () => prepareLegacyReview(id!),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });
}

export function useSaveLegacyReview(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (review: LegacySaveReviewRequest) =>
      legacyCasesService.save(id, review),
    onSuccess: (detail) => {
      client.setQueryData(["legacy-case", id], detail);
      void client.invalidateQueries({ queryKey: ["legacy-cases"] });
    },
  });
}

export function useExtractLegacyCase(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      legacyCasesService.extract(id, documentId),
    onSuccess: (detail) => client.setQueryData(["legacy-case", id], detail),
  });
}

export function useImportLegacyCase(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: LegacyImportRequest) =>
      legacyCasesService.import(id, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["legacy-case", id] });
      void client.invalidateQueries({ queryKey: ["legacy-cases"] });
      void client.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useLegacyImportedCase(caseId: string, enabled = true) {
  return useQuery({
    queryKey: ["legacy-import", caseId],
    queryFn: () => legacyCasesService.imported(caseId),
    enabled: enabled && Boolean(caseId),
  });
}

export function useLegacyPhysicians(id: string, search: string) {
  return useQuery({
    queryKey: ["legacy-physicians", id, search],
    queryFn: () => legacyCasesService.physicians(id, search),
    enabled: search.trim().length >= 2,
  });
}

export function useLegacyPatients(id: string, search: string) {
  return useQuery({
    queryKey: ["legacy-patients", id, search],
    queryFn: () => legacyCasesService.patients(id, search),
    enabled: search.trim().length >= 2,
  });
}

export function useLegacyPatientCandidates(
  id: string,
  review: LegacySaveReviewRequest
) {
  const patient =
    review.patient.action === "create" ? review.patient : undefined;
  return useQuery({
    queryKey: [
      "legacy-patient-candidates",
      id,
      review.sourceRowId,
      review.reportDocumentId,
      review.extractionId,
      patient?.firstName,
      patient?.lastName,
      patient?.dateOfBirth,
      patient?.sex,
      patient?.medicalRecordNumber,
    ],
    queryFn: () => legacyCasesService.patientCandidates(id, review),
    enabled: Boolean(patient),
  });
}

export function useAttachLegacyRequisition(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: { s3Key: string; originalFileName: string }) =>
      legacyCasesService.attachRequisition(id, payload),
    onSuccess: (detail) => client.setQueryData(["legacy-case", id], detail),
  });
}
